'use strict'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CircularProgress, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import RefreshIcon from '@material-ui/icons/Refresh'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import UndoIcon from '@material-ui/icons/Undo'
import RestoreIcon from '@material-ui/icons/Restore'
import { WORK_DAYS } from './scheduleUtils'
import { DEFAULT_CURVE, wallClockMinutes } from './crewEfficiency'
import { HMI_YARD, DEFAULT_CENTER, DEFAULT_ZOOM } from './mapUtils'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider,
  googleHybridProvider
} from '../../HerbicideSchedule/v3/maricopaBasemap'
import {
  PropertyDetailDrawer,
  buildGeoInsights,
  buildSidebarData,
  getDayAbbrev,
  normalizeRoutingRow,
  useRoutingPropertyDetailStyles
} from '../RoutingPropertiesTable'

const WEEK_TYPES = ['A', 'B']
const DAILY_TARGET_MINUTES = 480
const WEEKLY_TARGET_MINUTES = DAILY_TARGET_MINUTES * WORK_DAYS.length

const formatMinutes = (value) => {
  const minutes = Math.round(Number(value || 0))
  if (!minutes) return '0m'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (!hours) return `${mins}m`
  if (!mins) return `${hours}h`
  return `${hours}h ${mins}m`
}

const finiteNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const getStopLatLng = (stop = {}) => {
  const lat = finiteNumber(stop.latitude ?? stop.lat)
  const lng = finiteNumber(stop.longitude ?? stop.lng)
  if (lat == null || lng == null || (lat === 0 && lng === 0)) return null
  return [lat, lng]
}

const getWeekSummary = (summary, weekType) => {
  const existing = summary?.byWeek?.[weekType]
  if (existing) {
    return {
      stops: existing.stops || 0,
      serviceMin: existing.serviceMin || 0,
      driveMin: existing.driveMin || 0,
      laborMin: existing.laborMin ?? existing.totalMin ?? 0,
      otMin: existing.otMin || 0
    }
  }
  return { stops: 0, serviceMin: 0, driveMin: 0, laborMin: 0, otMin: 0 }
}

const getRefineRouteOptionId = (option) => {
  if (!option) return ''
  return option.id || (option.routeId ? `route:${option.routeId}` : (option.crewId ? `crew:${option.crewId}` : ''))
}

const getRefineRouteOptionLabel = (option) => {
  if (!option) return ''
  return option.displayName || option.routeName || option.crewName || ''
}

const getRefineRouteOptions = (payload = {}) => {
  const source = payload || {}
  return source.routes || source.crews || []
}

const isBiWeeklyStop = (stop = {}) => {
  const code = String(stop.frequencyCode || '').trim().toUpperCase()
  return stop.scheduleKind === 'biweekly' || code === 'BW' || code === 'BW_A' || code === 'BW_B'
}

const recomputeBucket = (bucket) => {
  if (!bucket) return
  const stops = bucket.stops || []
  bucket.stopCount = stops.length
  bucket.serviceMin = stops.reduce((sum, stop) => sum + Number(stop.serviceMin || 0), 0)
  bucket.totalMin = bucket.serviceMin + Number(bucket.driveMin || 0)
  bucket.otMin = Math.max(0, bucket.totalMin - DAILY_TARGET_MINUTES)
}

// Mirrors the server's per-bucket crew-size re-derivation: rescale each
// stop's wall-clock serviceMin from its man-minutes under the new crew size,
// then recompute the bucket aggregates (driveMin stays server-owned).
// Stops without man-minute data keep their baked serviceMin.
const applyCrewSizeToBucket = (bucket, crewSize, curve) => {
  if (!bucket) return
  ;(bucket.stops || []).forEach(stop => {
    if (Number(stop.serviceManMin) > 0) {
      stop.serviceMin = wallClockMinutes(Number(stop.serviceManMin), crewSize, curve)
      stop.crewSize = crewSize
    }
  })
  bucket.crewSize = crewSize
  recomputeBucket(bucket)
}

const recomputeCrewSummary = (crew) => {
  if (!crew) return
  const summary = crew.summary || (crew.summary = {})
  const byWeek = {}
  WEEK_TYPES.forEach(weekType => {
    const week = { stops: 0, serviceMin: 0, driveMin: 0, laborMin: 0, totalMin: 0, otMin: 0 }
    WORK_DAYS.forEach(day => {
      const dayBucket = crew.buckets?.[`${weekType}:${day}`]
      if (!dayBucket) return
      week.stops += Number(dayBucket.stopCount || 0)
      week.serviceMin += Number(dayBucket.serviceMin || 0)
      week.driveMin += Number(dayBucket.driveMin || 0)
      week.otMin += Number(dayBucket.otMin || 0)
    })
    week.totalMin = week.serviceMin + week.driveMin
    week.laborMin = week.totalMin
    byWeek[weekType] = week
  })
  summary.byWeek = byWeek
  summary.totalStops = byWeek.A.stops + byWeek.B.stops
  summary.totalServiceMin = byWeek.A.serviceMin + byWeek.B.serviceMin
  summary.totalDriveMin = byWeek.A.driveMin + byWeek.B.driveMin
  summary.totalMin = byWeek.A.totalMin + byWeek.B.totalMin
  summary.totalLaborMin = summary.totalMin
  summary.totalOTMin = byWeek.A.otMin + byWeek.B.otMin
}

const parseISODate = (value) => {
  if (typeof value !== 'string') return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

const formatShortDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  return `${date.toLocaleDateString('en-US', { month: 'short' })} ${date.getDate()}`
}

const formatDateRange = (min, max) => {
  if (!min || !max) return ''
  if (min.getTime() === max.getTime()) return formatShortDate(min)
  if (min.getMonth() === max.getMonth()) {
    return `${min.toLocaleDateString('en-US', { month: 'short' })} ${min.getDate()}–${max.getDate()}`
  }
  return `${formatShortDate(min)} – ${formatShortDate(max)}`
}

const todayYmd = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const addDaysYmd = (ymd, days) => {
  const date = parseISODate(ymd)
  if (!date) return null
  date.setDate(date.getDate() + days)
  return date
}

const deriveBucketDate = (bucket) => {
  const counts = {}
  ;(bucket?.stops || []).forEach(stop => {
    const key = typeof stop.date === 'string' ? stop.date.slice(0, 10) : null
    if (key) counts[key] = (counts[key] || 0) + 1
  })
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
  return best ? parseISODate(best[0]) : null
}

const getLoadTone = (totalMin, otMin) => {
  if (Number(otMin || 0) > 0) return { color: '#a9341d', bg: 'rgba(169,52,29,0.08)', border: 'rgba(169,52,29,0.24)' }
  if (Number(totalMin || 0) >= 420) return { color: '#8c5b00', bg: 'rgba(198,106,69,0.09)', border: 'rgba(198,106,69,0.22)' }
  return { color: 'var(--df-primary)', bg: 'rgba(31,94,58,0.08)', border: 'rgba(31,94,58,0.18)' }
}

const formatSuggestionTarget = (target = {}) => {
  const day = target.dayOfWeek ? target.dayOfWeek.slice(0, 3) : '?'
  const week = target.displayWeekType || target.weekType || 'Weekly'
  return week === 'Both' ? `${day} Weekly` : `${day} Wk ${week}`
}

function StatBlock ({ label, value, tone, subA, subB }) {
  const accentColor = tone === 'danger' ? '#a9341d' : tone === 'warn' ? '#8c5b00' : 'var(--df-ink)'
  
  return (
    <div style={{
      border: '1px solid var(--df-line)',
      background: 'var(--df-surface)',
      borderRadius: 8,
      padding: '8px 12px',
      minWidth: 100,
      flex: 1
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ marginTop: 2, fontSize: 18, fontWeight: 800, color: accentColor, fontFamily: 'var(--df-font-mono, monospace)' }}>{value}</div>
      {(subA !== undefined || subB !== undefined) && (
        <div style={{ marginTop: 4, display: 'flex', gap: 8, fontSize: 10, color: 'var(--df-ink-3)', fontWeight: 600 }}>
          <span>A: {subA}</span>
          <span>B: {subB}</span>
        </div>
      )}
    </div>
  )
}

function WeeklySummary ({ summary }) {
  if (!summary) return null
  const weekA = getWeekSummary(summary, 'A')
  const weekB = getWeekSummary(summary, 'B')
  const totalLaborMin = summary.totalLaborMin ?? summary.totalMin ?? 0
  
  return (
    <div style={{
      padding: '10px 16px',
      background: 'var(--df-surface)',
      borderBottom: '1px solid var(--df-line)',
      display: 'flex',
      gap: 10,
      alignItems: 'center'
    }}>
      <div style={{ width: 120, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--df-ink)' }}>Fortnight</div>
        <div style={{ fontSize: 11, color: 'var(--df-ink-4)', marginTop: 1 }}>per week →</div>
      </div>
      <StatBlock label="Total Stops" value={summary.totalStops || 0} subA={weekA.stops} subB={weekB.stops} />
      <StatBlock label="Drive Time" value={formatMinutes(summary.totalDriveMin)} tone={summary.totalDriveMin > 0 ? 'warn' : ''} subA={formatMinutes(weekA.driveMin)} subB={formatMinutes(weekB.driveMin)} />
      <StatBlock label="Service Hrs" value={formatMinutes(summary.totalServiceMin)} subA={formatMinutes(weekA.serviceMin)} subB={formatMinutes(weekB.serviceMin)} />
      <StatBlock label="Labor Hrs" value={formatMinutes(totalLaborMin)} tone={weekA.laborMin > WEEKLY_TARGET_MINUTES || weekB.laborMin > WEEKLY_TARGET_MINUTES ? 'warn' : ''} subA={formatMinutes(weekA.laborMin)} subB={formatMinutes(weekB.laborMin)} />
      <StatBlock label="Overtime" value={formatMinutes(summary.totalOTMin)} tone={summary.totalOTMin > 0 ? 'danger' : ''} subA={formatMinutes(weekA.otMin)} subB={formatMinutes(weekB.otMin)} />
    </div>
  )
}

const adaptStopToRoutingProperty = (stop = {}) => {
  const serviceHours = Number(stop.serviceMin || 0) / 60
  const futureVisits = Array.isArray(stop.futureVisits) && stop.futureVisits.length
    ? stop.futureVisits.map(visit => ({
        ...visit,
        estimatedHours: Number(visit.estimatedHours || 0) || serviceHours,
        crewLeaderName: visit.crewLeaderName || stop.assignedCrewName,
        statusName: visit.statusName || 'Scheduled'
      }))
    : (stop.date
        ? [{
            date: stop.date,
            dayOfWeek: stop.dayOfWeek,
            estimatedHours: serviceHours,
            crewLeaderName: stop.assignedCrewName,
            statusName: 'Scenario'
          }]
        : [])

  return normalizeRoutingRow({
    ...stop,
    name: stop.propertyName,
    propertyId: stop.aspirePropertyId,
    aspirePropertyId: stop.aspirePropertyId,
    address1: stop.address || '',
    address2: stop.city || '',
    estimatedHours: serviceHours,
    avgOnSiteHours: serviceHours,
    medianOnSiteHours: serviceHours,
    futureVisits,
    scheduledDays: stop.dayOfWeek ? [stop.dayOfWeek] : [],
    primaryCrewLeader: {
      name: stop.assignedCrewName,
      crewLeaderName: stop.assignedCrewName
    },
    completedOccurrences: 0,
    futureTicketCount: futureVisits.length,
    annualOccurrences: 0
  })
}

// Scenario stop payloads are thin — parked/unscheduled ones especially carry no
// address, visits, or financials, so the drawer would read as globally
// unscheduled. Lay the scenario context (day, crew, parked flags) over the live
// routing property so the drawer shows the real schedule underneath.
const mergeLivePropertyIntoScenarioStop = (adapted, liveProperty) => {
  const live = normalizeRoutingRow(liveProperty)
  return {
    ...live,
    name: adapted.name || live.name,
    scheduledDays: adapted.scheduledDays?.length ? adapted.scheduledDays : live.scheduledDays,
    futureVisits: adapted.futureVisits?.length ? adapted.futureVisits : live.futureVisits,
    primaryCrewLeader: adapted.primaryCrewLeader?.name ? adapted.primaryCrewLeader : live.primaryCrewLeader,
    scheduleLocked: !!adapted.scheduleLocked,
    parkedCrewName: adapted.parkedCrewName || null,
    parkedCrewInactive: adapted.parkedCrewInactive ?? null,
    parkedCrewDivision: adapted.parkedCrewDivision || null,
    scenarioUnscheduled: !!adapted.scenarioUnscheduled
  }
}

function Stat ({ label, value, tone }) {
  return (
    <div style={{
      border: '1px solid var(--df-line)',
      background: 'var(--df-surface)',
      borderRadius: 8,
      padding: '10px 12px'
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ marginTop: 3, fontSize: 18, fontWeight: 800, color: tone || 'var(--df-ink)' }}>{value}</div>
    </div>
  )
}

function StopCard ({ stop, saving, onToggleLock, onOpenDetails, isSelected, onPointerDown, onSelect, onHover }) {
  const selectStop = () => {
    if (onSelect) onSelect(stop)
    else if (onOpenDetails) onOpenDetails(stop)
  }

  return (
    <div 
      data-card
      data-card-id={stop.aspirePropertyId}
      onPointerDown={(e) => onPointerDown && onPointerDown(e, stop)}
      onMouseEnter={() => onHover && onHover(stop)}
      onMouseLeave={() => onHover && onHover(null)}
      style={{
        border: isSelected ? '2px solid var(--df-primary)' : '1px solid var(--df-line)',
        background: stop.scheduleLocked ? 'rgba(52,84,193,0.06)' : 'var(--df-surface)',
        borderRadius: 7,
        padding: '7px 8px',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 6,
        minWidth: 0,
        cursor: 'pointer',
        userSelect: 'none',
        touchAction: 'none'
      }}
      role="button"
      tabIndex="0"
      onClick={(e) => {
        if (e.defaultPrevented) return
        if (!onPointerDown) selectStop()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          selectStop()
        }
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--df-ink)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {stop.propertyName}
        </div>
        <div style={{ marginTop: 2, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: 'var(--df-ink-4)' }}>{formatMinutes(stop.serviceMin)}</span>
          {stop.frequencyCode && (
            <span className="rw-pill pill-freq">{stop.frequencyCode}</span>
          )}
          {stop.isVip && (
            <span className="rw-pill" style={{ background: 'rgba(164,80,47,0.12)', color: '#a4502f', padding: '1px 5px', fontSize: 9 }}>VIP</span>
          )}
          {stop.unplaced && (
            <span
              className="rw-pill"
              title="Ticket exists but is NOT on Aspire's schedule board — it will not appear on the crew's mobile app until placed"
              style={{ background: 'rgba(169,52,29,0.12)', color: '#a9341d', padding: '1px 5px', fontSize: 9, fontWeight: 800 }}
            >
              UNPLACED
            </span>
          )}
          {stop.scheduleLocked && (
            <LockIcon style={{ fontSize: 10, color: '#3454c1' }} />
          )}
        </div>
      </div>
      <button
        type="button"
        title={stop.scheduleLocked ? 'Unlock schedule' : 'Lock to current day/week'}
        disabled={saving}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          onToggleLock(stop)
        }}
        style={{
          width: 24,
          height: 24,
          borderRadius: 6,
          border: '1px solid var(--df-line-2)',
          background: stop.scheduleLocked ? '#eef1ff' : 'var(--df-surface)',
          color: stop.scheduleLocked ? '#3454c1' : 'var(--df-ink-4)',
          cursor: saving ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0
        }}
      >
        {stop.scheduleLocked ? <LockIcon style={{ fontSize: 12 }} /> : <LockOpenIcon style={{ fontSize: 12 }} />}
      </button>
    </div>
  )
}

const WEEKDAY_COLORS = {
  Monday: '#f59e0b',
  Tuesday: '#ef4444',
  Wednesday: '#ec4899',
  Thursday: '#8b5cf6',
  Friday: '#06b6d4'
}

function DayBucket ({ bucket, savingByPropertyId, onToggleLock, onOpenDetails, onSelectStop, selectedStopId, onPointerDown, isTarget, targetIndex, recentMove, onHoverStop, lightestDay, isFocused, onToggleFocus, pending, bucketDate: bucketDateProp, onCrewSizeChange, onCrewSizeReset }) {
  const dayColor = WEEKDAY_COLORS[bucket.dayOfWeek] || 'var(--df-line)'
  const tone = getLoadTone(bucket.totalMin, bucket.otMin)
  const stops = bucket.stops || []
  const otThreshold = 480 // 8 hours in minutes
  const loadPct = Math.min(100, (bucket.totalMin / otThreshold) * 100)
  const bucketDate = bucketDateProp || deriveBucketDate(bucket)
  const crewSize = finiteNumber(bucket.crewSize)
  const baseCrewSize = finiteNumber(bucket.baseCrewSize)
  const crewSizeOverridden = crewSize != null && baseCrewSize != null && crewSize !== baseCrewSize
  const crewSizeColor = crewSizeOverridden ? '#8c5b00' : 'var(--df-ink-3)'
  const stepBtnStyle = (disabled) => ({
    width: 16,
    height: 16,
    border: '1px solid var(--df-line-2)',
    borderRadius: 4,
    background: 'var(--df-surface)',
    color: disabled ? 'var(--df-ink-4)' : 'var(--df-ink-2)',
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0
  })

  return (
    <div
      data-drop={bucket.id}
      data-orient="v"
      onClick={(event) => {
        if (!onToggleFocus) return
        // Ignore clicks on stop cards and controls — those select/act, not focus
        if (event.target.closest('[data-card], button, input, a')) return
        onToggleFocus(bucket.id)
      }}
      title={isFocused ? 'Clear map focus' : 'Focus this day on the map'}
      style={{
        minWidth: 0,
        border: (isTarget || isFocused) ? `2px solid ${dayColor}` : `1px solid ${tone.border}`,
        background: tone.bg,
        borderRadius: 8,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        minHeight: 230,
        position: 'relative',
        transition: 'border 0.2s ease',
        borderTop: `4px solid ${dayColor}`,
        cursor: onToggleFocus ? 'pointer' : 'default'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: isFocused ? dayColor : 'var(--df-ink)', display: 'flex', gap: 5, alignItems: 'baseline' }}>
            {bucket.dayOfWeek.slice(0, 3)}
            {bucketDate && (
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--df-ink-4)' }}>{formatShortDate(bucketDate)}</span>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--df-ink-4)', marginTop: 1 }}>
            {stops.length} stops{pending ? ' · syncing…' : ''}
            {(() => {
              const unplacedCount = stops.filter(s => s.unplaced).length
              return unplacedCount ? <span style={{ color: '#a9341d', fontWeight: 800 }}> · {unplacedCount} unplaced</span> : null
            })()}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {recentMove ? (
            <div style={{
              fontSize: 10,
              fontWeight: 800,
              color: recentMove.type === 'add' ? 'var(--df-primary)' : '#a9341d',
              background: recentMove.type === 'add' ? 'rgba(31,94,58,0.1)' : 'rgba(169,52,29,0.1)',
              padding: '2px 6px',
              borderRadius: 4,
              animation: 'plpop 0.3s ease'
            }}>
              {recentMove.type === 'add' ? '+' : '-'}{recentMove.stops} stop{recentMove.stops > 1 ? 's' : ''}
              {recentMove.deltaMin ? ` · ${recentMove.type === 'add' ? '+' : '−'}${formatMinutes(recentMove.deltaMin)}` : ''}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: tone.color }}>{formatMinutes(bucket.totalMin)}</div>
              <div style={{ fontSize: 10, color: Number(bucket.otMin || 0) > 0 ? '#a9341d' : 'var(--df-ink-4)' }}>
                {Number(bucket.otMin || 0) > 0 ? `${formatMinutes(bucket.otMin)} OT` : 'OK'}
              </div>
            </>
          )}
          {crewSize != null && (
            <div
              title={`Crew size for this day — base ${baseCrewSize ?? crewSize} from route`}
              style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <button
                type="button"
                disabled={!onCrewSizeChange || crewSize <= 1}
                onClick={() => onCrewSizeChange && crewSize > 1 && onCrewSizeChange(bucket, crewSize - 1)}
                style={stepBtnStyle(!onCrewSizeChange || crewSize <= 1)}
              >
                −
              </button>
              <span style={{
                fontSize: 10,
                fontWeight: 800,
                minWidth: 14,
                textAlign: 'center',
                color: crewSizeColor,
                fontFamily: 'var(--df-font-mono, monospace)'
              }}>
                {crewSizeOverridden ? `${baseCrewSize}→${crewSize}` : crewSize}
              </span>
              <button
                type="button"
                disabled={!onCrewSizeChange || crewSize >= 6}
                onClick={() => onCrewSizeChange && crewSize < 6 && onCrewSizeChange(bucket, crewSize + 1)}
                style={stepBtnStyle(!onCrewSizeChange || crewSize >= 6)}
              >
                +
              </button>
              {bucket.crewSizeSource === 'override' && onCrewSizeReset && (
                <button
                  type="button"
                  onClick={() => onCrewSizeReset(bucket)}
                  title="Reset to route crew size"
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#8c5b00',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  reset
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div style={{ height: 4, background: 'rgba(0,0,0,0.05)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${loadPct}%`, background: tone.color, transition: 'width 0.3s ease' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minHeight: 0 }}>
        {stops.map((stop, i) => (
          <React.Fragment key={`${bucket.id}-${stop.aspirePropertyId}-${stop.date || stop.source || i}`}>
            {isTarget && targetIndex === i && (
              <div style={{ height: 40, border: '2px dashed var(--df-line-2)', borderRadius: 7 }} />
            )}
            <StopCard
              stop={stop}
              saving={!!savingByPropertyId[stop.aspirePropertyId]}
              onToggleLock={onToggleLock}
              onOpenDetails={onOpenDetails}
              onSelect={onSelectStop}
              isSelected={selectedStopId === stop.aspirePropertyId}
              onPointerDown={onPointerDown}
              onHover={onHoverStop}
            />
          </React.Fragment>
        ))}
        {isTarget && targetIndex >= stops.length && (
          <div style={{ height: 40, border: '2px dashed var(--df-line-2)', borderRadius: 7 }} />
        )}
        {!stops.length && !isTarget && (
          <div style={{
            border: '1px dashed var(--df-line-2)',
            color: 'var(--df-ink-4)',
            borderRadius: 7,
            padding: '24px 8px',
            fontSize: 11,
            textAlign: 'center'
          }}>
            Drop stops here
          </div>
        )}
      </div>

      <div
        style={{ marginTop: 'auto', paddingTop: 4, borderTop: '1px solid rgba(0,0,0,0.05)' }}
        title={bucket.serviceManMin ? `≈ ${formatMinutes(bucket.serviceManMin)} man-hours${stops[0]?.crewSize ? ` @ crew of ${stops[0].crewSize}` : ''}` : undefined}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          <span style={{ fontSize: 10, color: 'var(--df-ink-4)' }}>Svc {formatMinutes(bucket.serviceMin)}</span>
          <span style={{ fontSize: 10, color: 'var(--df-ink-4)', textAlign: 'right' }}>Drv {formatMinutes(bucket.driveMin)}</span>
        </div>
        {Number(bucket.noManMinCount || 0) > 0 && (
          <div style={{ fontSize: 9, color: 'var(--df-ink-4)', fontStyle: 'italic', marginTop: 3 }}>
            {bucket.noManMinCount} stop{Number(bucket.noManMinCount) > 1 ? 's' : ''} lack{Number(bucket.noManMinCount) > 1 ? '' : 's'} man-hour data
          </div>
        )}
        {Number(bucket.otMin || 0) > 0 && lightestDay && lightestDay !== bucket.dayOfWeek && (
          <div style={{ fontSize: 10, color: '#8c5b00', fontWeight: 700, marginTop: 3 }}>
            ↳ lightest: {lightestDay.slice(0, 3)}
          </div>
        )}
      </div>
    </div>
  )
}

function UnscheduledTray ({ stops, savingByPropertyId, onOpenDetails, onSelectStop, selectedStopId, onPointerDown, isTarget, targetIndex, recentMove, onHoverStop }) {
  return (
    <div 
      data-drop="unsched"
      data-orient="h"
      style={{
        padding: '12px 16px',
        background: 'var(--df-bg-2)',
        borderBottom: '1px solid var(--df-line)',
        display: 'flex',
        gap: 10,
        overflowX: 'auto',
        minHeight: 70,
        alignItems: 'center'
      }}
    >
      <div style={{ minWidth: 100, flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--df-ink-4)', textTransform: 'uppercase' }}>
          Unscheduled ({stops.length})
        </div>
        {recentMove && (
          <div style={{ 
            fontSize: 10, 
            fontWeight: 800, 
            color: recentMove.type === 'add' ? 'var(--df-primary)' : '#a9341d',
            marginTop: 4,
            animation: 'plpop 0.3s ease'
          }}>
            {recentMove.type === 'add' ? '+' : '-'}{recentMove.stops} stop{recentMove.stops > 1 ? 's' : ''}
            {recentMove.deltaMin ? ` · ${recentMove.type === 'add' ? '+' : '−'}${formatMinutes(recentMove.deltaMin)}` : ''}
          </div>
        )}
      </div>
      {stops.map((stop, i) => (
        <React.Fragment key={stop.aspirePropertyId}>
          {isTarget && targetIndex === i && (
            <div style={{ minWidth: 160, height: 40, border: '2px dashed var(--df-line-2)', borderRadius: 7 }} />
          )}
          <div style={{ minWidth: 160, maxWidth: 220 }}>
            <StopCard
              stop={stop}
              saving={!!savingByPropertyId[stop.aspirePropertyId]}
              onToggleLock={() => {}} // Can't lock unscheduled
              onOpenDetails={onOpenDetails}
              onSelect={onSelectStop}
              isSelected={selectedStopId === stop.aspirePropertyId}
              onPointerDown={onPointerDown}
              onHover={onHoverStop}
            />
          </div>
        </React.Fragment>
      ))}
      {isTarget && targetIndex >= stops.length && (
        <div style={{ minWidth: 160, height: 40, border: '2px dashed var(--df-line-2)', borderRadius: 7 }} />
      )}
      {!stops.length && !isTarget && (
        <div style={{ fontSize: 12, color: 'var(--df-ink-4)', fontStyle: 'italic' }}>
          All stops are scheduled.
        </div>
      )}
    </div>
  )
}

const plannerYardIcon = L.divIcon({
  className: 'refine-planner-yard-marker',
  html: `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;">
      <div style="width:30px;height:30px;border-radius:8px;background:#1f5e3a;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 11L12 4l9 7" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M5.5 10v9h13v-9" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.5 19v-5h5v5" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div style="margin-top:2px;padding:1px 5px;border-radius:4px;background:rgba(255,255,255,.92);color:#1f5e3a;font-size:9px;font-weight:800;letter-spacing:.06em;box-shadow:0 1px 3px rgba(0,0,0,.25);">YARD</div>
    </div>
  `,
  iconSize: [34, 48],
  iconAnchor: [17, 15]
})

function PlannerMapPanel ({ url, selectedCrew, selectedStopId, onSelectStop, onOpenDetails, focusedDayKey, hoveredStopId, onClearFocus }) {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const baseTileLayerRef = useRef(null)
  const maricopaTileLayerRef = useRef(null)
  const dataLayerRef = useRef(null)
  const [weekFilter, setWeekFilter] = useState('Both')
  const [routeGeometryByKey, setRouteGeometryByKey] = useState({})
  const [routeGeometryLoading, setRouteGeometryLoading] = useState(false)
  const effectiveFocusKey = focusedDayKey

  const selectedStopInfo = useMemo(() => {
    if (!selectedStopId || !selectedCrew) return null
    const scheduled = Object.values(selectedCrew.buckets || {}).flatMap(b => b.stops || [])
    const all = [...scheduled, ...(selectedCrew.unscheduledStops || [])]
    return all.find(stop => Number(stop.aspirePropertyId) === Number(selectedStopId)) || null
  }, [selectedCrew, selectedStopId])

  useEffect(() => {
    if (!focusedDayKey) return
    const [focusWeekType] = focusedDayKey.split(':')
    setWeekFilter(prev => (prev === 'Both' || prev === focusWeekType) ? prev : focusWeekType)
  }, [focusedDayKey])

  const dayRoutes = useMemo(() => {
    const buckets = selectedCrew?.buckets || {}
    return WEEK_TYPES.flatMap(weekType => (
      WORK_DAYS.map(day => {
        const bucket = buckets[`${weekType}:${day}`] || {}
        const stops = (bucket.stops || [])
          .map((stop, index) => ({ ...stop, _routeOrder: index + 1 }))
          .filter(stop => getStopLatLng(stop))
        return {
          key: `${weekType}:${day}`,
          weekType,
          day,
          color: WEEKDAY_COLORS[day] || 'var(--df-primary)',
          stops
        }
      })
    ))
  }, [selectedCrew])

  const visibleRoutes = useMemo(() => {
    return dayRoutes.filter(route => weekFilter === 'Both' || route.weekType === weekFilter)
  }, [dayRoutes, weekFilter])

  const geometryRequests = useMemo(() => {
    return visibleRoutes
      .map(route => {
        const stopCoords = route.stops.map(getStopLatLng).filter(Boolean)
        if (!stopCoords.length) return null
        return {
          key: route.key,
          coordinates: [
            [HMI_YARD.lng, HMI_YARD.lat],
            ...stopCoords.map(([lat, lng]) => [lng, lat]),
            [HMI_YARD.lng, HMI_YARD.lat]
          ]
        }
      })
      .filter(Boolean)
  }, [visibleRoutes])

  const geometryRequestKey = useMemo(() => {
    return JSON.stringify(geometryRequests.map(route => ({
      key: route.key,
      coordinates: route.coordinates
    })))
  }, [geometryRequests])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return
    const map = L.map(mapContainerRef.current, {
      center: [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
      zoom: DEFAULT_ZOOM,
      maxZoom: 28,
      zoomControl: true,
      attributionControl: false
    })
    mapRef.current = map

    applyBasemapProvider({
      map,
      tileLayerRef: baseTileLayerRef,
      provider: googleHybridProvider
    })

    let cancelled = false
    getLatestMaricopaProvider()
      .then((provider) => {
        if (cancelled || !mapRef.current) return
        applyBasemapProvider({
          map,
          tileLayerRef: maricopaTileLayerRef,
          provider
        })
      })
      .catch(() => {})

    setTimeout(() => {
      try { map.invalidateSize() } catch (_) {}
    }, 150)

    return () => {
      cancelled = true
      if (dataLayerRef.current) {
        try { map.removeLayer(dataLayerRef.current) } catch (_) {}
        dataLayerRef.current = null
      }
      if (maricopaTileLayerRef.current) {
        try { map.removeLayer(maricopaTileLayerRef.current) } catch (_) {}
        maricopaTileLayerRef.current = null
      }
      if (baseTileLayerRef.current) {
        try { map.removeLayer(baseTileLayerRef.current) } catch (_) {}
        baseTileLayerRef.current = null
      }
      try { map.remove() } catch (_) {}
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!geometryRequests.length) {
      setRouteGeometryByKey({})
      setRouteGeometryLoading(false)
      return () => { cancelled = true }
    }

    const fetchRoadGeometry = async () => {
      setRouteGeometryLoading(true)
      try {
        const response = await axios.post(`${url}/api/hmi/territory/route-geometry`, {
          routes: geometryRequests
        })
        if (!cancelled) {
          setRouteGeometryByKey(response.data?.routes || {})
        }
      } catch (error) {
        if (!cancelled) setRouteGeometryByKey({})
      } finally {
        if (!cancelled) setRouteGeometryLoading(false)
      }
    }

    fetchRoadGeometry()
    return () => { cancelled = true }
  }, [url, geometryRequestKey, geometryRequests])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (dataLayerRef.current) {
      try { map.removeLayer(dataLayerRef.current) } catch (_) {}
    }
    const layer = L.layerGroup().addTo(map)
    dataLayerRef.current = layer

    const bounds = []
    L.marker([HMI_YARD.lat, HMI_YARD.lng], {
      icon: plannerYardIcon,
      title: HMI_YARD.label,
      zIndexOffset: 900
    }).addTo(layer)
    bounds.push([HMI_YARD.lat, HMI_YARD.lng])

    const focusBounds = []
    visibleRoutes.forEach(route => {
      const isFocusedRoute = !effectiveFocusKey || route.key === effectiveFocusKey
      const latLngs = route.stops.map(getStopLatLng).filter(Boolean)
      const roadGeometry = routeGeometryByKey[route.key]?.geometry || []
      const routeLatLngs = roadGeometry.length
        ? roadGeometry.map(([lng, lat]) => [lat, lng])
        : [[HMI_YARD.lat, HMI_YARD.lng], ...latLngs, [HMI_YARD.lat, HMI_YARD.lng]]
      if (latLngs.length >= 1) {
        L.polyline(routeLatLngs, {
          color: route.color,
          weight: isFocusedRoute ? (effectiveFocusKey ? 4 : 3) : 2,
          opacity: isFocusedRoute ? (roadGeometry.length ? 0.82 : 0.52) : 0.12,
          dashArray: roadGeometry.length ? null : '6 6'
        }).addTo(layer)
      }
      route.stops.forEach(stop => {
        const latLng = getStopLatLng(stop)
        if (!latLng) return
        bounds.push(latLng)
        if (route.key === focusedDayKey) focusBounds.push(latLng)
        const isSelected = Number(stop.aspirePropertyId) === Number(selectedStopId)
        const isHovered = Number(stop.aspirePropertyId) === Number(hoveredStopId)
        if (isSelected) {
          // Halo behind the selected marker
          L.circleMarker(latLng, {
            radius: 17,
            fillColor: route.color,
            fillOpacity: 0.22,
            stroke: false,
            interactive: false
          }).addTo(layer)
        }
        const marker = L.circleMarker(latLng, {
          radius: isSelected ? 11 : isHovered ? 10 : 7,
          fillColor: route.color,
          fillOpacity: isFocusedRoute ? (isSelected ? 1 : 0.88) : 0.25,
          color: isSelected ? '#1f5e3a' : '#ffffff',
          weight: isSelected ? 5 : isHovered ? 3 : 2,
          opacity: (isFocusedRoute || isSelected) ? 1 : 0.4
        })
          .addTo(layer)
          .on('click', () => onSelectStop(stop))
        marker.bindTooltip(`${route.weekType} ${route.day.slice(0, 3)} #${stop._routeOrder} · ${stop.propertyName || stop.aspirePropertyId}`)
        if (isSelected || isHovered) marker.bringToFront()
      })
    })

    const fitTarget = (focusedDayKey && focusBounds.length)
      ? [[HMI_YARD.lat, HMI_YARD.lng], ...focusBounds]
      : bounds
    if (fitTarget.length > 1) {
      try { map.fitBounds(fitTarget, { padding: [24, 24], maxZoom: 14 }) } catch (_) {}
    } else {
      map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM)
    }
  }, [visibleRoutes, routeGeometryByKey, selectedStopId, onSelectStop, effectiveFocusKey, focusedDayKey, hoveredStopId])

  const stopCount = visibleRoutes.reduce((sum, route) => sum + route.stops.length, 0)

  return (
    <div style={{
      borderLeft: '1px solid var(--df-line)',
      background: 'var(--df-surface)',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '12px 14px',
        borderBottom: '1px solid var(--df-line)',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--df-ink)' }}>Crew Map</div>
          <div style={{ fontSize: 11, color: 'var(--df-ink-4)', marginTop: 1 }}>
            {stopCount} mapped stops · {routeGeometryLoading ? 'routing...' : 'road geometry'}
          </div>
        </div>
        {focusedDayKey && (
          <button
            type="button"
            onClick={() => onClearFocus && onClearFocus()}
            title="Clear day focus"
            style={{
              border: `1px solid ${WEEKDAY_COLORS[focusedDayKey.split(':')[1]] || 'var(--df-line-2)'}`,
              borderRadius: 6,
              background: 'var(--df-surface)',
              color: WEEKDAY_COLORS[focusedDayKey.split(':')[1]] || 'var(--df-ink)',
              fontSize: 11,
              fontWeight: 800,
              padding: '5px 7px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {focusedDayKey.split(':')[1]} focused ×
          </button>
        )}
        {['A', 'B', 'Both'].map(option => (
          <button
            key={option}
            type="button"
            onClick={() => {
              setWeekFilter(option)
              if (onClearFocus) onClearFocus()
            }}
            style={{
              border: '1px solid var(--df-line-2)',
              borderRadius: 6,
              background: weekFilter === option ? 'var(--df-primary)' : 'var(--df-surface)',
              color: weekFilter === option ? '#fff' : 'var(--df-ink-3)',
              fontSize: 11,
              fontWeight: 800,
              padding: '5px 7px',
              cursor: 'pointer'
            }}
          >
            {option === 'Both' ? 'Both' : `Wk ${option}`}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
        {selectedStopInfo && (
          <div style={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            zIndex: 1000,
            width: 248,
            background: 'var(--df-surface)',
            border: '1px solid var(--df-line)',
            borderRadius: 8,
            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
            padding: 10,
            animation: 'plpop 0.2s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 800, color: 'var(--df-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedStopInfo.propertyName || selectedStopInfo.aspirePropertyId}
              </div>
              <button
                type="button"
                onClick={() => onSelectStop(null)}
                title="Clear selection"
                style={{ border: 'none', background: 'transparent', color: 'var(--df-ink-4)', cursor: 'pointer', fontSize: 13, fontWeight: 800, padding: 0, lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            {(selectedStopInfo.address || selectedStopInfo.city) && (
              <div style={{ fontSize: 10, color: 'var(--df-ink-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[selectedStopInfo.address, selectedStopInfo.city].filter(Boolean).join(', ')}
              </div>
            )}
            <div style={{ marginTop: 7, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', fontSize: 10 }}>
              <span style={{ color: 'var(--df-ink-4)', fontWeight: 700 }}>Preferred</span>
              <span style={{ color: 'var(--df-ink)', fontWeight: 600 }}>
                {(() => {
                  const days = (selectedStopInfo.originalDaysOfWeek?.length ? selectedStopInfo.originalDaysOfWeek : [selectedStopInfo.dayOfWeek]).filter(Boolean)
                  const week = selectedStopInfo.originalWeekType || selectedStopInfo.weekType
                  if (!days.length) return '—'
                  return `${days.map(d => d.slice(0, 3)).join(', ')}${week ? ` · Wk ${week}` : ''}`
                })()}
              </span>
              <span style={{ color: 'var(--df-ink-4)', fontWeight: 700 }}>Frequency</span>
              <span style={{ color: 'var(--df-ink)', fontWeight: 600 }}>{selectedStopInfo.frequencyCode || selectedStopInfo.frequency || '—'}</span>
              {(selectedStopInfo.workTicketNumbers || []).length > 0 && (
                <>
                  <span style={{ color: 'var(--df-ink-4)', fontWeight: 700 }}>Ticket{selectedStopInfo.workTicketNumbers.length > 1 ? 's' : ''}</span>
                  <span style={{ color: 'var(--df-ink)', fontWeight: 600, fontFamily: 'var(--df-font-mono, monospace)' }}>
                    {selectedStopInfo.workTicketNumbers.join(', ')}
                  </span>
                </>
              )}
              {selectedStopInfo.unplaced && (
                <>
                  <span style={{ color: '#a9341d', fontWeight: 700 }}>Aspire</span>
                  <span style={{ color: '#a9341d', fontWeight: 700 }}>
                    Not on schedule board — won't reach the crew's mobile app
                  </span>
                </>
              )}
              <span style={{ color: 'var(--df-ink-4)', fontWeight: 700 }}>Est Hrs</span>
              <span style={{ color: 'var(--df-ink)', fontWeight: 600 }}>{formatMinutes(selectedStopInfo.serviceMin)}</span>
              {selectedStopInfo.serviceManMin != null && (
                <>
                  <span style={{ color: 'var(--df-ink-4)', fontWeight: 700 }}>Man-Hrs</span>
                  <span style={{ color: 'var(--df-ink)', fontWeight: 600 }}>
                    {formatMinutes(selectedStopInfo.serviceManMin)}{selectedStopInfo.crewSize ? ` @ ${selectedStopInfo.crewSize} crew` : ''}
                  </span>
                </>
              )}
              {selectedStopInfo.actualOnSiteManMin != null && (
                <>
                  <span style={{ color: 'var(--df-ink-4)', fontWeight: 700 }}>Act On-Site</span>
                  <span style={{ color: '#8c5b00', fontWeight: 700 }}>
                    {formatMinutes(selectedStopInfo.actualOnSiteManMin)} mh{selectedStopInfo.actualCrewCount ? ` · ${selectedStopInfo.actualCrewCount} men` : ''}
                  </span>
                </>
              )}
            </div>
            {onOpenDetails && (
              <button
                type="button"
                onClick={() => onOpenDetails(selectedStopInfo)}
                style={{
                  marginTop: 8,
                  width: '100%',
                  border: '1px solid var(--df-line-2)',
                  background: 'var(--df-bg)',
                  color: 'var(--df-ink-2)',
                  borderRadius: 6,
                  padding: '5px 8px',
                  fontSize: 10,
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                Full details →
              </button>
            )}
          </div>
        )}
        {!stopCount && (
          <div style={{
            position: 'absolute',
            inset: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: 'var(--df-ink-4)',
            fontSize: 12,
            fontWeight: 700,
            background: 'rgba(255,255,255,0.72)',
            border: '1px dashed var(--df-line-2)',
            borderRadius: 8
          }}>
            No coordinates for this view
          </div>
        )}
      </div>
      <div style={{
        padding: '9px 12px',
        borderTop: '1px solid var(--df-line)',
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap'
      }}>
        {WORK_DAYS.map(day => (
          <span key={day} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--df-ink-4)', fontWeight: 700 }}>
            <span style={{ width: 8, height: 8, borderRadius: 99, background: WEEKDAY_COLORS[day] }} />
            {day.slice(0, 3)}
          </span>
        ))}
      </div>
    </div>
  )
}

function SuggestionsStrip ({ selectedCrew, applyingSuggestionKey, isScenarioEditable, onApplySuggestion }) {
  const suggestions = selectedCrew?.suggestions || []
  if (!suggestions.length) return null

  return (
    <div style={{
      border: '1px solid var(--df-line)',
      background: 'var(--df-surface)',
      borderRadius: 8,
      padding: 12
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--df-ink)' }}>Suggestions</div>
        <div style={{ fontSize: 11, color: 'var(--df-ink-4)' }}>Locked stops excluded</div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 9
      }}>
        {suggestions.map((item, idx) => {
          const applyKey = `${item.aspirePropertyId}-${idx}`
          const applying = applyingSuggestionKey === applyKey
          return (
            <div
              key={`${item.aspirePropertyId}-${idx}`}
              style={{
                border: '1px solid var(--df-line)',
                borderRadius: 8,
                padding: 10,
                background: 'var(--df-bg)'
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--df-ink)' }}>{item.propertyName}</div>
              <div style={{ fontSize: 11, color: 'var(--df-ink-3)', marginTop: 4 }}>
                {formatSuggestionTarget(item.from)} → {formatSuggestionTarget(item.to)}
              </div>
              <div style={{ marginTop: 7, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="rw-pill pill-relief">{item.estimatedImpact?.otSavedMin || 0}m OT saved</span>
                <span className="rw-pill pill-dist">score {item.score}</span>
              </div>
              <button
                type="button"
                disabled={!isScenarioEditable || applying}
                onClick={() => onApplySuggestion(item, idx)}
                style={{
                  marginTop: 9,
                  width: '100%',
                  border: '1px solid var(--df-primary)',
                  background: isScenarioEditable ? 'var(--df-primary)' : 'var(--df-bg-2)',
                  color: isScenarioEditable ? '#fff' : 'var(--df-ink-4)',
                  borderRadius: 6,
                  padding: '7px 9px',
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: (!isScenarioEditable || applying) ? 'not-allowed' : 'pointer'
                }}
              >
                {applying ? 'Applying...' : 'Apply'}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function RefineRouteTab ({
  url,
  selectedScenarioId,
  isScenarioEditable,
  onSnackbar,
  requestedCrewRequest
}) {
  const detailClasses = useRoutingPropertyDetailStyles()
  const [analysis, setAnalysis] = useState(null)
  const [localAnalysis, setLocalAnalysis] = useState(null)
  // Ref mirror for synchronous reads in click-time handlers (avoids stale closure without extra deps)
  const localAnalysisRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [selectedCrewId, setSelectedCrewId] = useState('')
  const selectedCrewIdRef = useRef('')
  const [savingByPropertyId, setSavingByPropertyId] = useState({})
  const [applyingSuggestionKey, setApplyingSuggestionKey] = useState('')
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [selectedStopId, setSelectedStopId] = useState(null)
  const [propertyDetailTab, setPropertyDetailTab] = useState('overview')
  const [hoursView, setHoursView] = useState('visits')
  const [detailHistoryRange, setDetailHistoryRange] = useState('90')
  const [detailHistory, setDetailHistory] = useState({ loading: false, error: null, visits: [] })
  const [yardRoute, setYardRoute] = useState({ loading: false, error: null, data: null })
  const [hoveredVisit, setHoveredVisit] = useState(null)
  const [pinnedVisit, setPinnedVisit] = useState(null)
  const [visitTypeFilter, setVisitTypeFilter] = useState('all')

  const [undoStack, setUndoStack] = useState([])
  const [recentMoves, setRecentMoves] = useState({}) // { bucketKey: { type: 'add'|'remove', stops: n, deltaMin } }
  const [dragState, setDragState] = useState(null)
  const ghostRef = useRef(null)

  const [focusedDayKey, setFocusedDayKey] = useState(null)
  const [hoveredStopId, setHoveredStopId] = useState(null)
  const [pendingKeys, setPendingKeys] = useState({})
  const [anchorDate, setAnchorDate] = useState(todayYmd())

  useEffect(() => {
    setFocusedDayKey(null)
    setHoveredStopId(null)
  }, [selectedCrewId])

  // Keep ref mirrors in sync with state (written by every render so click handlers always see fresh values)
  localAnalysisRef.current = localAnalysis
  selectedCrewIdRef.current = selectedCrewId

  // Suppresses the synthetic click that fires on a bucket right after a drag ends there
  const dragJustEndedRef = useRef(false)

  // Stale-response guard: each fetchAnalysis call gets an id; only the latest one commits state
  const analysisRequestIdRef = useRef(0)

  // Debounce crew-size POSTs: timers keyed by `${resolvedCrewId}:${weekType}:${dayOfWeek}`
  // NOTE: the TIMER key includes crewId to prevent cross-crew debounce collisions.
  // The SPINNER key (pendingKeys state) stays `${weekType}:${dayOfWeek}` — that's what DayBucket reads.
  const crewSizeSaveTimers = useRef({})
  // Latest intended save entry per timer key: { size, crewId, routeId, weekType, dayOfWeek }
  // Captured at click time so the debounced timer fires with click-time crew identity.
  const crewSizePendingValues = useRef({})
  // Stable ref to flushCrewSizeSaves so fetchAnalysis can call it without a dependency cycle
  // (fetchAnalysis is defined before flushCrewSizeSaves but flushRef is always up to date).
  const flushCrewSizeSavesRef = useRef(null)

  // Clean up all crew-size timers on unmount
  useEffect(() => {
    return () => {
      Object.values(crewSizeSaveTimers.current).forEach(clearTimeout)
    }
  }, [])

  const toggleDayFocus = useCallback((key) => {
    if (dragJustEndedRef.current) return
    setFocusedDayKey(prev => (prev === key ? null : key))
  }, [])

  const hoverStop = useCallback((stop) => {
    setHoveredStopId(stop ? stop.aspirePropertyId : null)
  }, [])

  useEffect(() => {
    const keys = Object.keys(recentMoves)
    if (keys.length === 0) return
    const timer = setTimeout(() => {
      setRecentMoves({})
    }, 2600)
    return () => clearTimeout(timer)
  }, [recentMoves])

  const fetchAnalysis = useCallback(async (isRefresh = false) => {
    if (!selectedScenarioId) {
      setAnalysis(null)
      setLocalAnalysis(null)
      return
    }
    // Flush any pending debounced crew-size saves first so the GET sees persisted
    // intent — on reconciles AND manual refreshes (otherwise a pending save fires
    // ~500ms after a "reset to server" fetch and silently re-applies the override).
    // Flushing an empty map is a no-op, so initial loads are unaffected.
    // flushCrewSizeSavesRef is always current (set below after flushCrewSizeSaves is defined).
    if (flushCrewSizeSavesRef.current) {
      await flushCrewSizeSavesRef.current()
    }
    const requestId = ++analysisRequestIdRef.current
    if (!isRefresh) setLoading(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/refine/analyze`, {
        params: anchorDate ? { date: anchorDate } : {}
      })
      if (requestId !== analysisRequestIdRef.current) return
      const payload = response.data || null
      setAnalysis(payload)
      setLocalAnalysis(payload)
      const routeOptionIds = getRefineRouteOptions(payload).map(getRefineRouteOptionId).filter(Boolean)
      setSelectedCrewId(prev => (prev && routeOptionIds.includes(prev)) ? prev : (routeOptionIds[0] || ''))
    } catch (err) {
      if (requestId !== analysisRequestIdRef.current) return
      onSnackbar(err.response?.data?.message || err.message || 'Failed to load refine analysis', 'error')
    } finally {
      // Spinner clear is NOT behind the stale guard — stranding loading:true is worse than clearing it late.
      // Only the data set* calls above keep the stale guard.
      if (!isRefresh) setLoading(false)
    }
  }, [selectedScenarioId, url, onSnackbar, anchorDate])

  useEffect(() => {
    fetchAnalysis()
  }, [fetchAnalysis])

  const selectedCrew = useMemo(() => {
    return getRefineRouteOptions(localAnalysis).find(crew => getRefineRouteOptionId(crew) === selectedCrewId) || null
  }, [localAnalysis, selectedCrewId])

  const crewOptions = useMemo(() => {
    return getRefineRouteOptions(localAnalysis)
      .slice()
      .sort((a, b) => getRefineRouteOptionLabel(a).localeCompare(getRefineRouteOptionLabel(b)))
  }, [localAnalysis])

  // Honor "Refine this route" requests from the Map tab (preselect that route)
  const appliedRefineRequestRef = useRef(null)
  useEffect(() => {
    const request = requestedCrewRequest
    if (!request?.key || appliedRefineRequestRef.current === request.ts) return
    const exists = crewOptions.some(option => getRefineRouteOptionId(option) === request.key)
    if (exists) {
      setSelectedCrewId(request.key)
      appliedRefineRequestRef.current = request.ts
    }
  }, [requestedCrewRequest, crewOptions])

  const lightestByWeek = useMemo(() => {
    const result = {}
    WEEK_TYPES.forEach(weekType => {
      let best = null
      WORK_DAYS.forEach(day => {
        const dayBucket = selectedCrew?.buckets?.[`${weekType}:${day}`]
        const total = Number(dayBucket?.totalMin || 0)
        if (!best || total < best.total) best = { day, total }
      })
      result[weekType] = best?.day || null
    })
    return result
  }, [selectedCrew])

  const weekStartDates = localAnalysis?.meta?.weekStartDates || null

  // Render weeks in calendar order, not fixed A-then-B
  const orderedWeekTypes = useMemo(() => {
    if (weekStartDates?.A && weekStartDates?.B) {
      return weekStartDates.A <= weekStartDates.B ? ['A', 'B'] : ['B', 'A']
    }
    return WEEK_TYPES
  }, [weekStartDates])

  const weekDateRanges = useMemo(() => {
    const ranges = {}
    WEEK_TYPES.forEach(weekType => {
      const weekStart = weekStartDates?.[weekType] ? parseISODate(weekStartDates[weekType]) : null
      if (weekStart) {
        ranges[weekType] = formatDateRange(weekStart, addDaysYmd(weekStartDates[weekType], 4))
        return
      }
      const dates = WORK_DAYS
        .map(day => deriveBucketDate(selectedCrew?.buckets?.[`${weekType}:${day}`]))
        .filter(Boolean)
        .map(date => date.getTime())
      ranges[weekType] = dates.length
        ? formatDateRange(new Date(Math.min(...dates)), new Date(Math.max(...dates)))
        : ''
    })
    return ranges
  }, [selectedCrew, weekStartDates])

  const detailRows = useMemo(() => {
    return getRefineRouteOptions(analysis).flatMap(crew => (
      Object.values(crew.buckets || {}).flatMap(dayBucket => (
        (dayBucket.stops || []).map(adaptStopToRoutingProperty)
      ))
    ))
  }, [analysis])

  const geoInsights = useMemo(() => {
    return {
      ...buildGeoInsights(selectedProperty, detailRows),
      yardRoute: yardRoute.data,
      routeLoading: yardRoute.loading,
      routeError: yardRoute.error
    }
  }, [selectedProperty, detailRows, yardRoute])

  const sidebarData = useMemo(() => {
    return buildSidebarData(selectedProperty, detailHistory, [], geoInsights)
  }, [selectedProperty, detailHistory, geoInsights])

  const drawerDerived = useMemo(() => {
    const recentVisits = sidebarData.visitRows.filter(visit => visit.wAgo < 4)
    const priorVisits = sidebarData.visitRows.filter(visit => visit.wAgo >= 4 && visit.wAgo < 12)
    const recentAvg = recentVisits.length
      ? recentVisits.reduce((sum, visit) => sum + visit.hrs, 0) / recentVisits.length
      : sidebarData.kpis.avgHours
    const priorAvg = priorVisits.length
      ? priorVisits.reduce((sum, visit) => sum + visit.hrs, 0) / priorVisits.length
      : recentAvg
    return {
      trendPct: priorAvg ? Math.round(((recentAvg - priorAvg) / priorAvg) * 100) : 0,
      marginSpark: sidebarData.monthlyRows.map(month => month.rev + month.othRev - month.cost),
      visitsSpark: sidebarData.monthlyRows.map(month => month.visits),
      dayAbbrev: getDayAbbrev(sidebarData.scheduledDay),
      dominantService: sidebarData.serviceRows.slice().sort((a, b) => b.hours - a.hours)[0] || null
    }
  }, [sidebarData])

  // Keyed on the id (not the object) so live-data hydration of the selected
  // scenario stop doesn't refetch the same history.
  const selectedPropertyId = selectedProperty?.aspirePropertyId || selectedProperty?.propertyId || null

  useEffect(() => {
    if (!selectedPropertyId) {
      setDetailHistory({ loading: false, error: null, visits: [] })
      return
    }

    let cancelled = false
    const fetchDetailHistory = async () => {
      setDetailHistory(prev => ({ ...prev, loading: true, error: null }))
      try {
        const response = await axios.get(`${url}/api/aspire/routing/properties/${selectedPropertyId}/visit-history`, {
          params: { range: detailHistoryRange }
        })
        if (!cancelled) {
          setDetailHistory({ loading: false, error: null, visits: response.data?.visits || [] })
        }
      } catch (error) {
        if (!cancelled) {
          setDetailHistory({ loading: false, error: error.message, visits: [] })
        }
      }
    }

    fetchDetailHistory()
    return () => { cancelled = true }
  }, [url, selectedPropertyId, detailHistoryRange])

  useEffect(() => {
    if (!selectedPropertyId || !selectedProperty?.latitude || !selectedProperty?.longitude) {
      setYardRoute({ loading: false, error: null, data: null })
      return
    }

    let cancelled = false
    const fetchYardRoute = async () => {
      setYardRoute({ loading: true, error: null, data: null })
      try {
        const response = await axios.get(`${url}/api/aspire/routing/properties/${selectedPropertyId}/yard-route`)
        if (!cancelled) setYardRoute({ loading: false, error: null, data: response.data || null })
      } catch (error) {
        if (!cancelled) {
          setYardRoute({
            loading: false,
            error: error.response?.data?.message || error.response?.data?.error || error.message,
            data: null
          })
        }
      }
    }

    fetchYardRoute()
    return () => { cancelled = true }
  }, [url, selectedPropertyId, selectedProperty?.latitude, selectedProperty?.longitude])

  const bucket = useCallback((weekType, day) => {
    return selectedCrew?.buckets?.[`${weekType}:${day}`] || {
      id: `${weekType}:${day}`,
      weekType,
      dayOfWeek: day,
      stops: [],
      stopCount: 0,
      serviceMin: 0,
      driveMin: 0,
      totalMin: 0,
      otMin: 0
    }
  }, [selectedCrew])

  const toggleScheduleLock = async (stop) => {
    if (!selectedScenarioId || !isScenarioEditable || !stop?.aspirePropertyId) return
    setSavingByPropertyId(prev => ({ ...prev, [stop.aspirePropertyId]: true }))
    try {
      await axios.patch(
        `${url}/api/hmi/territory/properties/${stop.aspirePropertyId}`,
        { scheduleLocked: !stop.scheduleLocked },
        { params: { scenarioId: selectedScenarioId } }
      )
      onSnackbar(!stop.scheduleLocked ? 'Schedule locked' : 'Schedule unlocked', 'success')
      await fetchAnalysis(true)
    } catch (err) {
      onSnackbar(err.response?.data?.message || err.message || 'Failed to update schedule lock', 'error')
    } finally {
      setSavingByPropertyId(prev => ({ ...prev, [stop.aspirePropertyId]: false }))
    }
  }

  const openPropertyDetails = useCallback((stop) => {
    const adapted = adaptStopToRoutingProperty(stop)
    setSelectedProperty(adapted)
    setPropertyDetailTab('overview')
    setHoveredVisit(null)
    setPinnedVisit(null)
    const propertyId = Number(stop?.aspirePropertyId)
    if (!Number.isFinite(propertyId)) return
    // Hydrate the drawer with the live routing property underneath the scenario stop
    axios.get(`${url}/api/aspire/routing/properties/${propertyId}`)
      .then(response => {
        if (!response.data) return
        setSelectedProperty(prev => {
          const prevId = Number(prev?.aspirePropertyId || prev?.propertyId)
          if (prevId !== propertyId) return prev
          return mergeLivePropertyIntoScenarioStop(adapted, response.data)
        })
      })
      .catch(() => {}) // drawer still renders from the scenario payload alone
  }, [url])

  const selectStop = useCallback((stop) => {
    if (!stop) {
      setSelectedStopId(null)
      return
    }
    if (!stop.aspirePropertyId) return
    setSelectedStopId(stop.aspirePropertyId)
  }, [])

  const postSuggestionMove = useCallback(async (payload) => {
    const moveUrl = `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`
    try {
      return await axios.post(moveUrl, payload)
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === 'APPROVED_ROUTE_DAY') {
        const confirmed = window.confirm(err.response.data.message || 'This suggestion changes an approved route day. Apply it anyway?')
        if (!confirmed) throw err
        return axios.post(moveUrl, { ...payload, force: true })
      }
      if (err.response?.status === 409 && err.response?.data?.code === 'LOCKED_PROPERTY') {
        const lockedName = err.response.data.lockedCrewName || 'another crew'
        const confirmed = window.confirm(`This property is locked to ${lockedName}. Apply the schedule change anyway?`)
        if (!confirmed) throw err
        return axios.post(moveUrl, { ...payload, force: true })
      }
      throw err
    }
  }, [selectedScenarioId, url])

  const buildMoveTargetPayload = useCallback((payload) => {
    const target = { ...payload }
    if (selectedCrew?.routeId) {
      target.toRouteId = selectedCrew.routeId
      delete target.toCrewId
    } else {
      target.toCrewId = selectedCrew?.crewId || String(selectedCrewId || '').replace(/^crew:/, '')
    }
    return target
  }, [selectedCrew, selectedCrewId])

  const onPointerDown = (e, stop, sourceKeyOverride) => {
    if (e.button !== 0) return

    const rect = e.currentTarget.getBoundingClientRect()
    setDragState({
      stop,
      canDrag: isScenarioEditable && !stop.scheduleLocked,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      isDragging: false,
      sourceKey: sourceKeyOverride || (stop.dayOfWeek ? `${stop.weekType}:${stop.dayOfWeek}` : 'unsched'),
      targetKey: null,
      targetIndex: null
    })
  }

  useEffect(() => {
    if (!dragState) return

    const handlePointerMove = (e) => {
      if (!dragState.canDrag) return
      if (!dragState.isDragging) {
        const dist = Math.sqrt(Math.pow(e.clientX - dragState.startX, 2) + Math.pow(e.clientY - dragState.startY, 2))
        if (dist > 5) {
          setDragState(prev => ({ ...prev, isDragging: true }))
        }
        return
      }

      if (ghostRef.current) {
        ghostRef.current.style.left = `${e.clientX - dragState.offsetX}px`
        ghostRef.current.style.top = `${e.clientY - dragState.offsetY}px`
      }

      const elements = document.elementsFromPoint(e.clientX, e.clientY)
      const dropTarget = elements.find(el => el.hasAttribute('data-drop'))
      
      if (dropTarget) {
        const key = dropTarget.getAttribute('data-drop')
        const orient = dropTarget.getAttribute('data-orient')
        if (key === dragState.sourceKey) {
          setDragState(prev => ({ ...prev, targetKey: null, targetIndex: null }))
          return
        }
        const cards = Array.from(dropTarget.querySelectorAll('[data-card]'))
        let index = cards.length

        for (let i = 0; i < cards.length; i++) {
          const rect = cards[i].getBoundingClientRect()
          const mid = orient === 'v' ? rect.top + rect.height / 2 : rect.left + rect.width / 2
          const pos = orient === 'v' ? e.clientY : e.clientX
          if (pos < mid) {
            index = i
            break
          }
        }
        
        setDragState(prev => ({ ...prev, targetKey: key, targetIndex: index }))
      } else {
        setDragState(prev => ({ ...prev, targetKey: null, targetIndex: null }))
      }
    }

    const handlePointerUp = async (e) => {
      if (!dragState.isDragging) {
        // Tap selects; tapping an already-selected card opens the drawer.
        const tappedId = dragState.stop?.aspirePropertyId
        if (tappedId && Number(tappedId) === Number(selectedStopId)) {
          openPropertyDetails(dragState.stop)
        } else {
          selectStop(dragState.stop)
        }
        setDragState(null)
        return
      }

      const { stop, sourceKey, targetKey, targetIndex } = dragState
      setDragState(null)
      dragJustEndedRef.current = true
      setTimeout(() => { dragJustEndedRef.current = false }, 0)

      if (!targetKey || (sourceKey === targetKey)) {
        return
      }

      const previousAnalysis = localAnalysis
      const newAnalysis = JSON.parse(JSON.stringify(previousAnalysis))
      const crew = getRefineRouteOptions(newAnalysis).find(c => getRefineRouteOptionId(c) === selectedCrewId)
      if (!crew) return
      
      // Remove from source
      let movedStop = null
      if (sourceKey === 'unassigned') {
        const idx = (newAnalysis.unassignedStops || []).findIndex(s => s.aspirePropertyId === stop.aspirePropertyId)
        if (idx !== -1) movedStop = newAnalysis.unassignedStops.splice(idx, 1)[0]
      } else if (sourceKey === 'unsched') {
        const idx = (crew.unscheduledStops || []).findIndex(s => s.aspirePropertyId === stop.aspirePropertyId)
        if (idx !== -1) movedStop = crew.unscheduledStops.splice(idx, 1)[0]
      } else {
        const b = crew.buckets?.[sourceKey]
        const idx = (b?.stops || []).findIndex(s => s.aspirePropertyId === stop.aspirePropertyId)
        if (idx !== -1) movedStop = b.stops.splice(idx, 1)[0]
      }

      if (!movedStop) return

      // Add to target
      if (targetKey === 'unsched') {
        if (!crew.unscheduledStops) crew.unscheduledStops = []
        crew.unscheduledStops.splice(targetIndex, 0, movedStop)
      } else {
        if (!crew.buckets) crew.buckets = {}
        if (!crew.buckets[targetKey]) {
          const [wt, day] = targetKey.split(':')
          crew.buckets[targetKey] = { id: targetKey, weekType: wt, dayOfWeek: day, stops: [] }
        }
        if (!crew.buckets[targetKey].stops) crew.buckets[targetKey].stops = []
        crew.buckets[targetKey].stops.splice(targetIndex, 0, movedStop)
      }

      // Optimistic estimate: recompute affected day stats (drive stays server-owned/stale)
      if (sourceKey !== 'unsched') recomputeBucket(crew.buckets?.[sourceKey])
      if (targetKey !== 'unsched') recomputeBucket(crew.buckets?.[targetKey])
      recomputeCrewSummary(crew)

      const stopIsBiWeekly = isBiWeeklyStop(movedStop)
      const originalWeekType = movedStop.originalWeekType || null
      const sourceWeekType = sourceKey === 'unsched' ? null : sourceKey.split(':')[0]
      const targetWeekType = targetKey === 'unsched' ? null : targetKey.split(':')[0]

      if (sourceKey !== 'unassigned') {
        const inversePayload = buildMoveTargetPayload({
          aspirePropertyId: stop.aspirePropertyId,
          toDaysOfWeek: sourceKey === 'unsched' ? [] : [sourceKey.split(':')[1]],
          toWeekType: sourceKey === 'unsched' ? null : (stopIsBiWeekly ? sourceWeekType : originalWeekType),
          moveReason: 'planner_undo'
        })
        setUndoStack(prev => [...prev, { snapshot: previousAnalysis, inversePayload }])
      }
      setLocalAnalysis(newAnalysis)
      const deltaMin = Number(movedStop.serviceMin || 0)
      setRecentMoves({
        [sourceKey]: { type: 'remove', stops: 1, deltaMin },
        [targetKey]: { type: 'add', stops: 1, deltaMin }
      })
      setPendingKeys({ [sourceKey]: true, [targetKey]: true })

      // Server call
      try {
        const isUnsched = targetKey === 'unsched'
        const toWt = isUnsched ? null : (stopIsBiWeekly ? targetWeekType : originalWeekType)
        const toDay = isUnsched ? [] : [targetKey.split(':')[1]]
        
        const payload = buildMoveTargetPayload({
          aspirePropertyId: stop.aspirePropertyId,
          toDaysOfWeek: toDay,
          toWeekType: toWt,
          moveReason: 'planner_drag'
        })
        await postSuggestionMove(payload)
        onSnackbar('Move successful', 'success')
        await fetchAnalysis(true) // Reconcile
      } catch (err) {
        setLocalAnalysis(previousAnalysis || analysis)
        if (sourceKey !== 'unassigned') setUndoStack(prev => prev.slice(0, -1))
        onSnackbar(err.response?.data?.message || err.message || 'Failed to move stop', 'error')
      } finally {
        setPendingKeys({})
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setDragState(null)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [dragState, localAnalysis, selectedCrewId, selectedStopId, postSuggestionMove, fetchAnalysis, analysis, onSnackbar, isScenarioEditable, selectStop, openPropertyDetails, buildMoveTargetPayload])

  const handleUndo = async () => {
    if (undoStack.length === 0) return
    const last = undoStack[undoStack.length - 1]
    setUndoStack(prevStack => prevStack.slice(0, -1))
    setLocalAnalysis(last.snapshot)
    try {
      await postSuggestionMove(last.inversePayload)
      onSnackbar('Move undone', 'success')
      await fetchAnalysis(true)
    } catch (err) {
      onSnackbar(err.response?.data?.message || err.message || 'Failed to undo move', 'error')
      await fetchAnalysis(true)
    }
  }

  // Shared save routine used by both the debounced timer and flushCrewSizeSaves.
  // entry: { size, crewId, routeId, weekType, dayOfWeek, remove? }
  // spinnerKey: `${weekType}:${dayOfWeek}` — what DayBucket's pending prop reads.
  // Returns a Promise that never rejects (errors are snackbar'd + refetch'd internally).
  const saveCrewSizeOverride = useCallback((entry) => {
    const { size, crewId, routeId, weekType, dayOfWeek, remove } = entry
    const spinnerKey = `${weekType}:${dayOfWeek}`
    const payload = { crewId, routeId, dayOfWeek, weekType, crewSize: size }
    if (remove) payload.remove = true
    return axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crew-day-override`, payload)
      .then(res => {
        const serverOverrides = res.data?.crewDayOverrides
        setLocalAnalysis(prev => {
          if (!prev) return prev
          const updated = JSON.parse(JSON.stringify(prev))
          if (serverOverrides !== undefined) {
            if (!updated.meta) updated.meta = {}
            updated.meta.crewDayOverrides = serverOverrides
          }
          // For remove: apply base values; for override: mark source.
          // Look up by the original selectedCrewId (which is what all option finders use),
          // captured via selectedCrewIdRef at the time the save fires.
          const crew = getRefineRouteOptions(updated).find(c =>
            (routeId && c.routeId === routeId)
            || (crewId && c.crewId === crewId)
            || getRefineRouteOptionId(c) === selectedCrewIdRef.current
          )
          const b = crew?.buckets?.[spinnerKey]
          if (b) {
            if (remove) {
              const curve = updated?.meta?.crewEfficiencyCurve || DEFAULT_CURVE
              const base = finiteNumber(b.baseCrewSize)
              if (base != null) {
                applyCrewSizeToBucket(b, base, curve)
                b.crewSize = base
              }
              b.crewSizeSource = b.baseCrewSizeSource || 'aspire_route'
              recomputeCrewSummary(crew)
            } else {
              b.crewSizeSource = 'override'
            }
          }
          return updated
        })
      })
      .catch(err => {
        onSnackbar(err.response?.data?.message || err.message || 'Failed to update crew size', 'error')
        fetchAnalysis(true)
      })
      .finally(() => {
        setPendingKeys(prev => {
          const next = { ...prev }
          delete next[spinnerKey]
          return next
        })
      })
  }, [selectedScenarioId, url, fetchAnalysis, onSnackbar])

  // Flush all pending debounced saves immediately (used before a refresh reconcile so the GET
  // sees already-persisted intent). Returns Promise.allSettled of all in-flight POSTs.
  const flushCrewSizeSaves = useCallback(() => {
    const promises = Object.keys(crewSizeSaveTimers.current).map(timerKey => {
      clearTimeout(crewSizeSaveTimers.current[timerKey])
      delete crewSizeSaveTimers.current[timerKey]
      const entry = crewSizePendingValues.current[timerKey]
      delete crewSizePendingValues.current[timerKey]
      if (!entry) return Promise.resolve()
      return saveCrewSizeOverride(entry)
    })
    return Promise.allSettled(promises)
  }, [saveCrewSizeOverride])
  // Keep the ref in sync so fetchAnalysis (defined earlier) can always call the latest flush.
  flushCrewSizeSavesRef.current = flushCrewSizeSaves

  const changeCrewSize = useCallback((targetBucket, nextSize, remove = false) => {
    if (!selectedScenarioId || !isScenarioEditable || !targetBucket?.dayOfWeek) return
    const spinnerKey = `${targetBucket.weekType}:${targetBucket.dayOfWeek}`
    const clamped = Math.max(1, Math.min(6, Math.round(Number(nextSize) || 0)))
    if (!remove && clamped === Number(targetBucket.crewSize)) return

    // Resolve crew identity at CLICK TIME from the live localAnalysis ref — not from targetBucket
    // (buckets never carry crewId/routeId) and not from selectedCrewId string alone (route-linked
    // crews have selectedCrewId === 'route:<id>' which no simple regex strips correctly).
    const currentAnalysis = localAnalysisRef.current
    const currentCrewId = selectedCrewIdRef.current
    const resolvedCrew = getRefineRouteOptions(currentAnalysis).find(c => getRefineRouteOptionId(c) === currentCrewId)
    const resolvedCrewId = resolvedCrew?.crewId || String(currentCrewId || '').replace(/^crew:/, '')
    const resolvedRouteId = resolvedCrew?.routeId || null

    // Timer key is crew-scoped to prevent cross-crew debounce collisions.
    // Spinner key stays week:day — that's what DayBucket's pending prop reads.
    const timerKey = `${resolvedCrewId || currentCrewId}:${targetBucket.weekType}:${targetBucket.dayOfWeek}`

    // Step 1: instant local math on every click — always reads latest localAnalysis via functional setState
    setLocalAnalysis(prev => {
      if (!prev) return prev
      const newAnalysis = JSON.parse(JSON.stringify(prev))
      const crew = getRefineRouteOptions(newAnalysis).find(c => getRefineRouteOptionId(c) === currentCrewId)
      const clonedBucket = crew?.buckets?.[spinnerKey]
      if (!clonedBucket) return prev

      // Preserve base source the first time an override is applied (before it was 'override')
      if (!remove && !clonedBucket.baseCrewSizeSource && clonedBucket.crewSizeSource !== 'override') {
        clonedBucket.baseCrewSizeSource = clonedBucket.crewSizeSource
      }

      const curve = newAnalysis?.meta?.crewEfficiencyCurve || DEFAULT_CURVE
      const effectiveSize = remove ? (finiteNumber(clonedBucket.baseCrewSize) || clamped) : clamped
      applyCrewSizeToBucket(clonedBucket, effectiveSize, curve)
      clonedBucket.crewSizeSource = remove ? (clonedBucket.baseCrewSizeSource || 'aspire_route') : 'override'
      recomputeCrewSummary(crew)
      return newAnalysis
    })

    // Step 2: per-key pending spinner (keyed by weekType:dayOfWeek for DayBucket)
    setPendingKeys(prev => ({ ...prev, [spinnerKey]: true }))

    // For remove: cancel any pending debounced save for this bucket+crew, POST right away
    if (remove) {
      clearTimeout(crewSizeSaveTimers.current[timerKey])
      delete crewSizeSaveTimers.current[timerKey]
      delete crewSizePendingValues.current[timerKey]

      saveCrewSizeOverride({
        size: clamped,
        crewId: resolvedCrewId,
        routeId: resolvedRouteId,
        weekType: targetBucket.weekType,
        dayOfWeek: targetBucket.dayOfWeek,
        remove: true
      })
      return
    }

    // Step 3: debounce the POST for ± clicks — capture click-time crew identity + final size
    crewSizePendingValues.current[timerKey] = {
      size: clamped,
      crewId: resolvedCrewId,
      routeId: resolvedRouteId,
      weekType: targetBucket.weekType,
      dayOfWeek: targetBucket.dayOfWeek
    }
    clearTimeout(crewSizeSaveTimers.current[timerKey])
    crewSizeSaveTimers.current[timerKey] = setTimeout(() => {
      delete crewSizeSaveTimers.current[timerKey]
      const entry = crewSizePendingValues.current[timerKey]
      delete crewSizePendingValues.current[timerKey]
      if (entry) saveCrewSizeOverride(entry)
    }, 500)
  }, [selectedScenarioId, isScenarioEditable, saveCrewSizeOverride])

  const resetCrewSize = useCallback((targetBucket) => {
    return changeCrewSize(targetBucket, targetBucket?.baseCrewSize, true)
  }, [changeCrewSize])

  const applySuggestion = async (suggestion, index) => {
    if (!selectedScenarioId || !isScenarioEditable || !suggestion?.aspirePropertyId) return
    const key = `${suggestion.aspirePropertyId}-${index}`
    setApplyingSuggestionKey(key)
    try {
      const payload = {
        aspirePropertyId: suggestion.aspirePropertyId,
        toDaysOfWeek: suggestion.to?.dayOfWeek ? [suggestion.to.dayOfWeek] : [],
        toWeekType: suggestion.to?.weekType || null,
        moveReason: 'refine_suggestion'
      }
      if (suggestion.routeId || selectedCrew?.routeId) {
        payload.toRouteId = suggestion.routeId || selectedCrew.routeId
      } else {
        payload.toCrewId = suggestion.crewId || selectedCrew?.crewId || String(selectedCrewId || '').replace(/^crew:/, '')
      }
      await postSuggestionMove(payload)
      onSnackbar('Suggestion applied', 'success')
      await fetchAnalysis()
    } catch (err) {
      if (err.response?.status === 409) {
        onSnackbar('Suggestion cancelled', 'info')
      } else {
        onSnackbar(err.response?.data?.message || err.message || 'Failed to apply suggestion', 'error')
      }
    } finally {
      setApplyingSuggestionKey('')
    }
  }

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--df-bg)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <style>{`
        @keyframes plpop {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div style={{
        flexShrink: 0,
        padding: '12px 18px',
        borderBottom: '1px solid var(--df-line)',
        background: 'var(--df-surface)',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--df-ink)' }}>Crew Schedule Planner</div>
          <div style={{ fontSize: 11, color: 'var(--df-ink-4)', marginTop: 2 }}>
            Drag and drop stops to balance load across weeks and days.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginRight: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)', textTransform: 'uppercase' }}>Week of</span>
          <input
            type="date"
            value={anchorDate}
            min={todayYmd()}
            onChange={(event) => {
              const next = event.target.value
              if (next) setAnchorDate(next)
            }}
            title="Anchor the fortnight view to the week containing this date"
            style={{
              border: '1px solid var(--df-line-2)',
              borderRadius: 6,
              background: 'var(--df-surface)',
              color: 'var(--df-ink)',
              fontSize: 12,
              fontFamily: 'inherit',
              padding: '5px 7px'
            }}
          />
          {anchorDate !== todayYmd() && (
            <button
              className="btn btn-ghost"
              style={{ padding: '5px 8px', fontSize: 11 }}
              onClick={() => setAnchorDate(todayYmd())}
              title="Back to today"
            >
              Today
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, marginRight: 12 }}>
          <button
            className="btn btn-ghost"
            style={{ padding: '6px 10px', fontSize: 12 }}
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            title="Undo last move"
          >
            <UndoIcon style={{ fontSize: 16, marginRight: 4 }} />
            Undo
          </button>
          <button
            className="btn btn-ghost"
            style={{ padding: '6px 10px', fontSize: 12 }}
            onClick={() => fetchAnalysis()}
            disabled={loading}
            title="Reset to server state"
          >
            <RestoreIcon style={{ fontSize: 16, marginRight: 4 }} />
            Reset
          </button>
        </div>

        <Autocomplete
          size="small"
          disableClearable
          options={crewOptions}
          value={selectedCrew || null}
          getOptionLabel={getRefineRouteOptionLabel}
          getOptionSelected={(option, value) => getRefineRouteOptionId(option) === getRefineRouteOptionId(value)}
          onChange={(event, nextCrew) => {
            const nextId = getRefineRouteOptionId(nextCrew)
            if (nextId) setSelectedCrewId(nextId)
          }}
          style={{
            minWidth: 280
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Search routes"
              InputProps={{
                ...params.InputProps,
                style: {
                  ...params.InputProps.style,
                  background: 'var(--df-surface)',
                  color: 'var(--df-ink)',
                  fontSize: 12,
                  fontFamily: 'inherit'
                }
              }}
            />
          )}
        />
        <button
          className="btn btn-ghost"
          style={{ padding: '7px 10px', fontSize: 12 }}
          onClick={() => fetchAnalysis()}
          disabled={loading}
        >
          <RefreshIcon style={{ fontSize: 15 }} />
        </button>
      </div>

      <WeeklySummary summary={selectedCrew?.summary} />

      {loading && !localAnalysis ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress style={{ color: 'var(--df-primary)' }} />
        </div>
      ) : (
        <div style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <UnscheduledTray
            stops={selectedCrew?.unscheduledStops || []}
            savingByPropertyId={savingByPropertyId}
            onOpenDetails={(stop) => openPropertyDetails({ ...stop, scenarioUnscheduled: true })}
            onSelectStop={selectStop}
            selectedStopId={selectedStopId}
            onPointerDown={onPointerDown}
            isTarget={dragState?.targetKey === 'unsched'}
            targetIndex={dragState?.targetIndex}
            recentMove={recentMoves['unsched']}
            onHoverStop={hoverStop}
          />

          {(localAnalysis?.unassignedStops || []).length > 0 && (
            <div style={{
              padding: '10px 16px',
              background: 'rgba(169,52,29,0.05)',
              borderBottom: '1px solid var(--df-line)',
              display: 'flex',
              gap: 10,
              overflowX: 'auto',
              alignItems: 'center'
            }}>
              <div style={{ minWidth: 110, flexShrink: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#a9341d', textTransform: 'uppercase' }}>
                  Unassigned ({localAnalysis.unassignedStops.length})
                </div>
                <div style={{ fontSize: 10, color: 'var(--df-ink-4)', marginTop: 1 }}>
                  No crew — drag to a day to assign to {getRefineRouteOptionLabel(selectedCrew) || 'the selected route'}
                </div>
              </div>
              {localAnalysis.unassignedStops.map(stop => (
                <div
                  key={stop.aspirePropertyId}
                  style={{ minWidth: 160, maxWidth: 220, flexShrink: 0 }}
                  title={stop.parkedCrewName
                    ? `Parked on "${stop.parkedCrewName}"${stop.parkedCrewInactive ? ' (inactive crew)' : ''}${stop.parkedCrewDivision ? ` · ${stop.parkedCrewDivision}` : ''} — not on any active maintenance board${stop.nextVisitDate ? ` · next ticket ${stop.nextVisitDate}` : ''}`
                    : `No crew assigned${stop.nextVisitDate ? ` · next ticket ${stop.nextVisitDate}` : ''}`}
                >
                  {stop.parkedCrewName && (
                    <div style={{ fontSize: 9, fontWeight: 800, color: '#8c5b00', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ⚠ parked: {stop.parkedCrewName}
                    </div>
                  )}
                  <StopCard
                    stop={stop}
                    saving={false}
                    onToggleLock={() => {}}
                    onOpenDetails={openPropertyDetails}
                    onSelect={selectStop}
                    isSelected={selectedStopId === stop.aspirePropertyId}
                    onPointerDown={(e, s) => onPointerDown(e, s, 'unassigned')}
                    onHover={hoverStop}
                  />
                </div>
              ))}
            </div>
          )}

          {(selectedCrew?.beyondWindowStops || []).length > 0 && (
            <div style={{
              padding: '8px 16px',
              background: 'var(--df-bg-2)',
              borderBottom: '1px solid var(--df-line)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--df-ink-4)', textTransform: 'uppercase' }}>
                Beyond this fortnight ({selectedCrew.beyondWindowStops.length})
              </span>
              {selectedCrew.beyondWindowStops.map(stop => (
                <button
                  key={stop.aspirePropertyId}
                  type="button"
                  onClick={() => stop.nextVisitDate && setAnchorDate(stop.nextVisitDate)}
                  title={`Next visit ${stop.nextVisitDate || 'unknown'} — click to jump to that week`}
                  style={{
                    border: '1px solid var(--df-line-2)',
                    borderRadius: 6,
                    background: 'var(--df-surface)',
                    color: 'var(--df-ink-3)',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '3px 8px',
                    cursor: 'pointer',
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {stop.propertyName} <span style={{ color: 'var(--df-ink-4)', fontFamily: 'var(--df-font-mono, monospace)' }}>{stop.nextVisitDate ? formatShortDate(parseISODate(stop.nextVisitDate)) : ''}</span>
                </button>
              ))}
            </div>
          )}

          <div style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 416px',
            overflow: 'hidden'
          }}>
            <div style={{ overflow: 'auto', padding: 16 }}>
              {selectedCrew ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {orderedWeekTypes.map(weekType => {
                    const weekSummary = getWeekSummary(selectedCrew.summary, weekType)
                    const weekOtMin = Number(weekSummary.otMin || 0)
                    return (
                      <div key={weekType}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          marginBottom: 10
                        }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--df-ink)' }}>
                            Week {weekType}{weekDateRanges[weekType] ? ` · ${weekDateRanges[weekType]}` : ''}
                          </div>
                          <div style={{ height: 1, background: 'var(--df-line)', flex: 1 }} />
                          <div style={{ fontSize: 11, color: 'var(--df-ink-4)', fontWeight: 600 }}>
                            {weekSummary.stops || 0} stops · {formatMinutes(weekSummary.driveMin || 0)} drive · {formatMinutes(weekSummary.serviceMin || 0)} svc · {formatMinutes(weekSummary.laborMin || 0)} labor · {' '}
                            <span style={{ color: weekOtMin > 0 ? '#a9341d' : 'var(--df-ink-4)', fontWeight: weekOtMin > 0 ? 800 : 600 }}>
                              {weekOtMin > 0 ? `${formatMinutes(weekOtMin)} OT` : 'no OT'}
                            </span>
                          </div>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, minmax(180px, 1fr))',
                          gap: 10
                        }}>
                          {WORK_DAYS.map((day, dayIndex) => (
                            <DayBucket
                              key={`${weekType}-${day}`}
                              bucketDate={weekStartDates?.[weekType] ? addDaysYmd(weekStartDates[weekType], dayIndex) : null}
                              bucket={bucket(weekType, day)}
                              savingByPropertyId={savingByPropertyId}
                              onToggleLock={toggleScheduleLock}
                              onOpenDetails={openPropertyDetails}
                              onSelectStop={selectStop}
                              selectedStopId={selectedStopId}
                              onPointerDown={onPointerDown}
                              isTarget={dragState?.targetKey === `${weekType}:${day}`}
                              targetIndex={dragState?.targetIndex}
                              recentMove={recentMoves[`${weekType}:${day}`]}
                              onHoverStop={hoverStop}
                              lightestDay={lightestByWeek[weekType]}
                              isFocused={focusedDayKey === `${weekType}:${day}`}
                              onToggleFocus={toggleDayFocus}
                              pending={!!pendingKeys[`${weekType}:${day}`]}
                              onCrewSizeChange={isScenarioEditable ? changeCrewSize : null}
                              onCrewSizeReset={isScenarioEditable ? resetCrewSize : null}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  })}
                  <SuggestionsStrip
                    selectedCrew={selectedCrew}
                    applyingSuggestionKey={applyingSuggestionKey}
                    isScenarioEditable={isScenarioEditable}
                    onApplySuggestion={applySuggestion}
                  />
                </div>
              ) : (
                <div style={{ padding: 24, color: 'var(--df-ink-4)' }}>No assigned crews found for this scenario.</div>
              )}
            </div>

            <PlannerMapPanel
              url={url}
              selectedCrew={selectedCrew}
              selectedStopId={selectedStopId}
              onSelectStop={selectStop}
              onOpenDetails={openPropertyDetails}
              focusedDayKey={focusedDayKey}
              hoveredStopId={hoveredStopId}
              onClearFocus={() => setFocusedDayKey(null)}
            />
          </div>
        </div>
      )}

      {dragState?.isDragging && (
        <div 
          ref={ghostRef}
          style={{
            position: 'fixed',
            width: 200,
            pointerEvents: 'none',
            zIndex: 10000,
            opacity: 0.8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            left: 0,
            top: 0
          }}
        >
          <StopCard stop={dragState.stop} saving={false} onToggleLock={() => {}} onOpenDetails={() => {}} />
        </div>
      )}

      <PropertyDetailDrawer
        open={!!selectedProperty}
        onClose={() => setSelectedProperty(null)}
        classes={detailClasses}
        selectedProperty={selectedProperty}
        sidebarData={sidebarData}
        geoInsights={geoInsights}
        activeTab={propertyDetailTab}
        onTabChange={setPropertyDetailTab}
        hoursView={hoursView}
        setHoursView={setHoursView}
        detailHistoryRange={detailHistoryRange}
        setDetailHistoryRange={setDetailHistoryRange}
        detailHistory={detailHistory}
        hoveredVisit={hoveredVisit}
        setHoveredVisit={setHoveredVisit}
        pinnedVisit={pinnedVisit}
        setPinnedVisit={setPinnedVisit}
        visitTypeFilter={visitTypeFilter}
        setVisitTypeFilter={setVisitTypeFilter}
        trendPct={drawerDerived.trendPct}
        marginSpark={drawerDerived.marginSpark}
        visitsSpark={drawerDerived.visitsSpark}
        dayAbbrev={drawerDerived.dayAbbrev}
        dominantService={drawerDerived.dominantService}
        propertyOpportunities={{ loading: false, data: [], error: null }}
        isProblematic={false}
      />
    </div>
  )
}
